import { Controller } from "@hotwired/stimulus"

// Coordinates the native scanner with the existing one-product-at-a-time
// routine setup flow. Scanned products are intentionally saved one at a time
// after their schedule is chosen so an interrupted queue never loses products
// the user already finished configuring.
export default class extends Controller {
  static targets = ["form", "error", "exit"]

  connect() {
    this.queue = []
    this.total = 0
    this.savedCount = 0
  }

  addScannedProducts(event) {
    if (event.detail?.status !== "completed") return

    const products = Array.from(event.detail.products || [])
      .map(product => this.normalizeProduct(product))
      .filter(product => product.id)

    this.queue = Array.from(new Map(products.map(product => [product.id, product])).values())
    if (this.queue.length === 0) return

    this.total = this.queue.length
    this.savedCount = 0
    this.hideError()
    this.showCurrentProduct()
  }

  async submit(event) {
    if (this.queue.length === 0) return

    event.preventDefault()
    if (this.submitting) return

    this.submitting = true
    this.hideError()

    try {
      const formData = new FormData(this.formTarget)
      formData.append("source", "scanner")

      const response = await fetch(this.formTarget.action, {
        method: "POST",
        body: formData,
        headers: {
          "Accept": "application/json",
          "X-CSRF-Token": this.csrfToken(),
          "X-Requested-With": "XMLHttpRequest"
        }
      })

      const payload = await response.json().catch(() => ({}))
      if (!response.ok) {
        this.showError(payload.message || "We couldn’t add that product. Please try again.")
        this.resetSubmissionState()
        return
      }

      this.queue.shift()
      this.savedCount += 1

      if (this.queue.length > 0) {
        this.resetSubmissionState()
        this.showCurrentProduct()
      } else {
        this.finishQueue()
      }
    } catch (error) {
      console.error("Error adding scanned product to routine:", error)
      this.showError("We couldn’t add that product. Please try again.")
      this.resetSubmissionState()
    }
  }

  skipCurrent() {
    if (this.queue.length === 0 || this.submitting) return

    this.queue.shift()
    this.hideError()

    if (this.queue.length > 0) {
      this.resetSubmissionState()
      this.showCurrentProduct()
    } else {
      this.finishQueue()
    }
  }

  normalizeProduct(product) {
    return {
      id: Number(product.product_id || product.productId),
      brandName: product.brand_name || product.brandName || "",
      productName: product.product_name || product.productName || "",
      imageUrl: product.image_url || product.imageUrl || ""
    }
  }

  showCurrentProduct() {
    const product = this.queue[0]
    this.formTarget.reset()

    this.dispatch("product-selected", {
      detail: {
        product,
        position: this.total - this.queue.length + 1,
        total: this.total
      }
    })
  }

  finishQueue() {
    this.dispatch("queue-finished")

    if (this.savedCount > 0 && this.hasExitTarget) {
      this.exitTarget.click()
    }
  }

  resetSubmissionState() {
    this.submitting = false
    this.dispatch("ready")
  }

  showError(message) {
    if (!this.hasErrorTarget) return

    this.errorTarget.textContent = message
    this.errorTarget.classList.remove("hidden")
  }

  hideError() {
    if (!this.hasErrorTarget) return

    this.errorTarget.textContent = ""
    this.errorTarget.classList.add("hidden")
  }

  csrfToken() {
    return document.querySelector('meta[name="csrf-token"]')?.content
  }
}
